import { RevenuePopupSettings } from "@pages/Property/PropertyPage/RevenuePopupSettings";
import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { NumberInput } from "@src/components/ui-components/Inputs/NumberInput/NumberInput";
import { SelectInput } from "@src/components/ui-components/Selectors/SelectInput/SelectInput";
import { Stack } from "@src/components/ui-components/Stack/Stack";
import { NumberUtilities } from "@src/utils/numberUtilities";
import { t } from "i18next";
import { AllTenantTypes, TenantType } from "to-be-removed/LocalData/Property/Units/TenantType";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { SelectInputData } from "../../../../../components/deprecated/SelectInputField/SelectInputField";
import { TextInputField } from "../../../../../components/deprecated/TextInputField/TextInputField";
import { Utilities } from "../../../../../utils/utilities";
import { IncomeData } from "../../../Property/Income/IncomeData";
import { UnitIndexRegulation } from "../../../Property/Units/UnitIndexRegulation";
import { UnitJointArea } from "../../../Property/Units/UnitJointArea";
import { UnitType } from "../../../Property/Units/UnitType";
import { DataObjectFieldComparator } from "../../DataObjects/DataObjectFieldComparator";
import { DataObjectType } from "../../DataObjects/DataObjectHelper";
import { DataObjectField, DecodeStringContext, InputElementContext } from "../DataObjectField";
import { FirebaseDataSelectField, FirebaseDataSelectFieldOption } from "../Generic/FirebaseDataSelectField";

export class TenantTypeField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<TenantType, FirebaseDataType> {
  override encode = (value: TenantType) => value.id;
  override decode = (value: any) => {
    if (typeof value === "object" && value.id && value.name) {
      return value;
    } else if (typeof value === "string") {
      return this.getValueForKey(value);
    }
    return undefined;
  };
  override convertValueToSelectInput(value: TenantType): SelectInputData {
    return new SelectInputData(value.id, value.name);
  }
  override getValueForKey(key: string): TenantType | undefined {
    return AllTenantTypes.find((type) => type.id === key);
  }
  override getOptions(): FirebaseDataSelectFieldOption[] {
    return AllTenantTypes.map((status) => {
      return {
        key: "" + status.id,
        label: status.name,
      };
    });
  }
  override getReadableStringRepresentation(value: TenantType): string {
    return value.name;
  }
  override isValueValid = (value: any): boolean => {
    return typeof value === "object" && value.id && value.name;
  };
}

export class UnitDataTypeField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<UnitType, FirebaseDataType> {
  override encode = (value: UnitType) => value.id;
  override decode = (value: any) => {
    return UnitType.getType(value);
  };
  override convertValueToSelectInput(value: UnitType): SelectInputData {
    return new SelectInputData(value.id + "", value.title);
  }
  override getValueForKey(key: string): UnitType | undefined {
    return UnitType.getType(key);
  }
  override getOptions(): FirebaseDataSelectFieldOption[] {
    return UnitType.ALL_TYPES.map((status) => {
      return {
        key: "" + status.id,
        label: status.title,
      };
    });
  }
  override getReadableStringRepresentation(value: UnitType): string {
    return value.title;
  }
  override isValueValid = (value: any): boolean => {
    return value instanceof UnitType;
  };
}

export class UnitDataIncomeDataField<FirebaseDataType extends DataObjectType> extends DataObjectField<IncomeData, FirebaseDataType> {
  encodeString = (value: IncomeData) => {
    return value.sumOfIncomes(12) + "";
  };
  decodeString = (value: string, context?: DecodeStringContext | undefined): IncomeData | undefined => {
    const valueNumber = parseFloat(value);
    if (isNaN(valueNumber)) {
      return undefined;
    }
    const data = new IncomeData();
    data.areaRent = [{ description: "", periodType: 12, quantity: 1, sum: valueNumber }];
    return data;
  };
  encode = (value: IncomeData): any => {
    return {
      areaRent: value.areaRent,
      parkingRent: value.parkingRent,
      otherRent: value.otherRent,
      totalPeriod: 12,
    };
  };
  decode = (value: any): IncomeData | undefined => {
    if (typeof value === "object") {
      const incomeData = new IncomeData();
      incomeData.setup(value);
      return incomeData;
    }
    return undefined;
  };
  override getReadableStringRepresentation(value: IncomeData, displayAsRaw?: boolean): string {
    if (displayAsRaw) return value.sumOfIncomes(12) + "";
    return NumberUtilities.formatValueToString(value.sumOfIncomes(12), "currency", undefined, 0);
  }
  allComparators: DataObjectFieldComparator[] = ["is-known", "is-unknown", "equals", "not-equals"];

  override singleInputElement = (
    value: IncomeData | undefined,
    setValue: (value: IncomeData | undefined) => void,
    context?: InputElementContext
  ): JSX.Element => {
    const income = value?.sumOfIncomes(12) ?? 0;
    const incomeText = Utilities.formatNumberOneDecimal(income) + ",-";
    if (!context?.unit) return <></>;

    return (
      <Button
        type="secondary"
        title={incomeText}
        width={context?.width}
        onClick={() => Popup.present((id) => <RevenuePopupSettings runBeforeSave={(data) => setValue(data)} id={id} unit={context.unit!} />)}
      />
    );
  };

  override isValueValid = (value: any): boolean => {
    return value instanceof IncomeData;
  };
}

export class UnitDataIndexRegulationField<FirebaseDataType extends DataObjectType> extends DataObjectField<UnitIndexRegulation, FirebaseDataType> {
  encodeString = (value: UnitIndexRegulation) => {
    return value.active + ":" + value.percentage;
  };
  decodeString = (value: string, context?: DecodeStringContext | undefined): UnitIndexRegulation | undefined => {
    const split = value.split(":");
    if (split.length === 2) {
      const active = split[0] === "true";
      const percentage = parseFloat(split[1]);
      if (!isNaN(percentage)) {
        return { active, percentage };
      }
    }
    return undefined;
  };
  encode = (value: UnitIndexRegulation): any => {
    return value;
  };
  decode = (value: any): UnitIndexRegulation | undefined => {
    if (typeof value === "object") {
      return {
        active: typeof value.active === "boolean" ? value.active : false,
        percentage: typeof value.percentage === "number" ? value.percentage : 0,
      };
    }
    return undefined;
  };
  getReadableStringRepresentation(value: UnitIndexRegulation): string {
    return value.active ? value.percentage + "%" : t("main.notRegulated");
  }
  allComparators: DataObjectFieldComparator[] = ["is-known", "is-unknown"];

  override singleInputElement = (
    value: UnitIndexRegulation | undefined,
    setValue: (value: UnitIndexRegulation | undefined) => void,
    context?: InputElementContext
  ): JSX.Element => {
    return (
      <div>
        <Checkbox
          value={value?.active ?? false}
          onChange={(checked) => setValue({ active: checked, percentage: checked ? (value?.percentage ?? 100) : undefined })}
          description={t("main.isIndexRegulated")}
        />
        {value?.active && (
          <TextInputField
            marginTop={16}
            type="number"
            value={value.percentage !== undefined ? value.percentage + "" : ""}
            onChange={(text) => {
              const value = parseInt(text);
              if (!isNaN(value)) setValue({ active: true, percentage: value });
              else setValue({ active: true, percentage: undefined });
            }}
          />
        )}
      </div>
    );
  };

  override isValueValid = (value: any): boolean => {
    return typeof value === "object" && typeof value.active === "boolean";
  };
}

export class UnitDataJointAreaField<FirebaseDataType extends DataObjectType> extends DataObjectField<UnitJointArea, FirebaseDataType> {
  encodeString = (value: UnitJointArea) => {
    return value.type + ":" + value.value;
  };
  decodeString = (value: string, context?: DecodeStringContext | undefined): UnitJointArea | undefined => {
    const split = value.split(":");
    if (split.length === 2) {
      const type = split[0];
      const value = parseFloat(split[1]);
      if ((type === "area" || type === "percentage") && !isNaN(value)) {
        return { type: type, value: type === "area" ? value : 0 };
      }
    }
    return undefined;
  };
  encode = (value: UnitJointArea): any => {
    return value;
  };
  decode = (value: any): UnitJointArea | undefined => {
    if (typeof value === "object") {
      return {
        type: typeof value.type === "string" ? value.type : "area",
        value: typeof value.value === "number" ? value.value : undefined,
      };
    }
    return undefined;
  };
  getReadableStringRepresentation(value: UnitJointArea): string {
    return value.value ? value.value + (value.type === "area" ? "m²" : "%") : t("main.none");
  }
  allComparators: DataObjectFieldComparator[] = ["is-known", "is-unknown"];

  override singleInputElement = (
    value: UnitJointArea | undefined,
    setValue: (value: UnitJointArea | undefined) => void,
    context?: InputElementContext
  ): JSX.Element => {
    return (
      <Stack direction="column" spacing={4}>
        <NumberInput
          value={value?.value}
          placeholder={t("main.enterNumber")}
          onChange={(valueNumber) => {
            setValue({ type: value?.type ?? "area", value: valueNumber });
          }}
        />
        <SelectInput
          options={[
            { value: "area", label: t("main.area") },
            { value: "percentage", label: t("main.percentage") },
          ]}
          isMulti={false}
          value={value?.type ?? "area"}
          onChange={(selectedOption) =>
            selectedOption && setValue({ type: selectedOption === "percentage" ? selectedOption : "area", value: value?.value ?? 0 })
          }
          width="100%"
        />
      </Stack>
    );
  };

  override isValueValid = (value: any): boolean => {
    return typeof value === "object" && typeof value.type === "string" && typeof value.value === "number";
  };
}
